import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Users, Settings } from 'lucide-react'
import { useSocket } from '../hooks/useSocket'
import { useAuthStore } from '../hooks/useAuthStore'
import { useUIStore } from '../hooks/useUIStore'
import { useTranslation } from 'react-i18next'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import DynamicGameLoader from '../components/DynamicGameLoader'
import SidePanel from '../components/SidePanel'

const GamePage = () => {
  const { gameId } = useParams()
  const navigate = useNavigate()
  const { currentGame } = useSocket()
  const { user } = useAuthStore()
  const { t } = useTranslation('game')
  useDocumentTitle(currentGame?.name || t('page.loadingGame'))
  const gamePanelExpanded = useUIStore((state) => state.gamePanelExpanded)
  const setGamePanelExpanded = useUIStore((state) => state.setGamePanelExpanded)

  useEffect(() => {
    if (!currentGame || currentGame.id !== gameId) {
      navigate('/lobby')
    }
  }, [currentGame, gameId, navigate])

  // The panel toggle lives in the global header (Layout.jsx) now; reset the
  // shared flag per game so it doesn't reopen stale on the next one
  useEffect(() => {
    return () => setGamePanelExpanded(false)
  }, [gameId, setGamePanelExpanded])

  if (!currentGame) {
    return (
      <div className="h-full bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">{t('page.loadingGame')}</div>
      </div>
    )
  }

  return (
    <div className="w-full h-full bg-gray-900 flex flex-col overflow-hidden">
      {/* Players & game settings — standard right-side overlay panel,
          toggled from the header menu */}
      <SidePanel
        isOpen={gamePanelExpanded}
        title={t('page.playersAndSettings')}
        onClose={() => setGamePanelExpanded(false)}
      >
        <div className="p-4">
          <div className="grid grid-cols-1 gap-6">
            {/* Players Panel */}
            <div className="glass-effect rounded-xl p-4">
              <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" />
                {t('page.players')} ({currentGame.players?.length || 0}/{currentGame.maxPlayers})
              </h3>

              <div className="space-y-2 max-h-48 overflow-y-auto">
                {currentGame.players?.map((player, index) => {
                  const redSeaColors = ['bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-orange-500'];
                  const isRedSea = currentGame.type === 'red-sea';
                  const playerColor = isRedSea ? redSeaColors[index % redSeaColors.length] : 'bg-gradient-to-br from-blue-500 to-purple-600';

                  return (
                    <div key={player.id || player} className="flex items-center gap-2 p-2 bg-gray-700/50 rounded-lg text-sm">
                      <div className={`w-6 h-6 ${playerColor} rounded-full flex items-center justify-center text-white text-xs font-medium border border-white/20 flex-shrink-0`}>
                        {(player.username || player).slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-xs font-medium truncate">
                          {player.username || player}
                          {(player.id || player) === currentGame.host && (
                            <span className="ml-1 text-yellow-400 text-xs">•</span>
                          )}
                          {(player.id || player) === user?.id && (
                            <span className="ml-1 text-blue-400 text-xs">•</span>
                          )}
                        </div>
                      </div>
                      <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
                    </div>
                  );
                })}

                {Array.from({
                  length: currentGame.maxPlayers - (currentGame.players?.length || 0)
                }).map((_, index) => (
                  <div key={`empty-${index}`} className="flex items-center gap-2 p-2 bg-gray-700/20 rounded-lg border border-dashed border-gray-600 text-xs">
                    <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <div className="text-gray-400 text-xs">{t('page.waiting')}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Game Settings Panel */}
            <div className="glass-effect rounded-xl p-4">
              <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
                <Settings className="w-4 h-4" />
                {t('page.gameSettings')}
              </h3>

              <div className="space-y-2 max-h-48 overflow-y-auto">
                {currentGame.settings && Object.keys(currentGame.settings).length > 0 ? (
                  Object.entries(currentGame.settings).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center p-2 bg-gray-700/50 rounded-lg text-xs">
                      <span className="text-gray-400 capitalize">
                        {key.replace(/([A-Z])/g, ' $1')}
                      </span>
                      <span className="text-white font-medium">{value}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-400 text-xs p-2">{t('page.noCustomSettings')}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </SidePanel>

      {/* Game Container - Takes remaining space */}
      <div className="flex-1 overflow-hidden">
        <DynamicGameLoader currentGame={currentGame} />
      </div>
    </div>
  )
}

export default GamePage